import { cn } from '@/lib/cn';
import type { Easing } from 'motion/react';
import * as m from 'motion/react-m';
import { useEffect, useId, useMemo, useState, type FC } from 'react';
import type {
  EasingPreset,
  EvidenceCircleLoaderProps,
} from './evidence-circle-loader.types';

// ---------------------------------------------------------------------------
// Easing preset → Motion value mapping
// ---------------------------------------------------------------------------

const EASING_MAP: Record<EasingPreset, Easing> = {
  easeInOut: 'easeInOut',
  circInOut: 'circInOut',
  backInOut: 'backInOut',
  anticipate: 'anticipate',
  soft: [0.45, 0, 0.55, 1],
  elastic: [0.34, 1.56, 0.64, 1],
  snappy: [0.76, 0, 0.24, 1],
};

// ---------------------------------------------------------------------------
// ViewBox: 0 0 48 48 — center at (24, 24)
// ---------------------------------------------------------------------------

const CENTER = 24;
const DEFAULT_RADIUS = 8;
const DEFAULT_STROKE_WIDTH = 2.5;

// ---------------------------------------------------------------------------
// Keyframe timeline — 13 stops per property
// ---------------------------------------------------------------------------

const TIMES = [0, 0.08, 0.16, 0.2, 0.3, 0.4, 0.48, 0.52, 0.64, 0.74, 0.82, 0.88, 1];

// ---------------------------------------------------------------------------
// Position generator
// ---------------------------------------------------------------------------

interface Pos {
  cx: number;
  cy: number;
}

function buildKeyframes(spread: number, formation: 'diamond' | 'square') {
  const s = spread;
  const c: Pos = { cx: CENTER, cy: CENTER };

  // Venn of 2: left / right
  const v2a: Pos = { cx: CENTER - 6 * s, cy: CENTER };
  const v2b: Pos = { cx: CENTER + 6 * s, cy: CENTER };

  // Venn of 3: equilateral triangle
  const v3a: Pos = { cx: CENTER, cy: CENTER - 7 * s };
  const v3b: Pos = { cx: CENTER - 6 * s, cy: CENTER + 4 * s };
  const v3c: Pos = { cx: CENTER + 6 * s, cy: CENTER + 4 * s };

  // Venn of 4: diamond or square
  const v4 =
    formation === 'square'
      ? {
          a: { cx: CENTER - 6 * s, cy: CENTER - 6 * s } as Pos,
          b: { cx: CENTER + 6 * s, cy: CENTER - 6 * s } as Pos,
          c: { cx: CENTER + 6 * s, cy: CENTER + 6 * s } as Pos,
          d: { cx: CENTER - 6 * s, cy: CENTER + 6 * s } as Pos,
        }
      : {
          a: { cx: CENTER, cy: CENTER - 8 * s } as Pos,
          b: { cx: CENTER + 8 * s, cy: CENTER } as Pos,
          c: { cx: CENTER, cy: CENTER + 8 * s } as Pos,
          d: { cx: CENTER - 8 * s, cy: CENTER } as Pos,
        };

  // prettier-ignore
  return [
    { // Circle A — always visible
      cx: [v2a.cx, v2a.cx, c.cx, c.cx, v3a.cx, v3a.cx, c.cx, c.cx, v4.a.cx, v4.a.cx, c.cx, c.cx, v2a.cx],
      cy: [v2a.cy, v2a.cy, c.cy, c.cy, v3a.cy, v3a.cy, c.cy, c.cy, v4.a.cy, v4.a.cy, c.cy, c.cy, v2a.cy],
      op: [1,      1,      1,    1,    1,      1,      1,    1,    1,       1,       1,    1,    1      ],
    },
    { // Circle B — visible in Venn2, Venn3, Venn4
      cx: [v2b.cx, v2b.cx, c.cx, c.cx, v3b.cx, v3b.cx, c.cx, c.cx, v4.b.cx, v4.b.cx, c.cx, c.cx, v2b.cx],
      cy: [v2b.cy, v2b.cy, c.cy, c.cy, v3b.cy, v3b.cy, c.cy, c.cy, v4.b.cy, v4.b.cy, c.cy, c.cy, v2b.cy],
      op: [1,      1,      0,    0,    1,      1,      0,    0,    1,       1,       0,    0,    1      ],
    },
    { // Circle C — visible in Venn3, Venn4
      cx: [c.cx, c.cx, c.cx, c.cx, v3c.cx, v3c.cx, c.cx, c.cx, v4.c.cx, v4.c.cx, c.cx, c.cx, c.cx],
      cy: [c.cy, c.cy, c.cy, c.cy, v3c.cy, v3c.cy, c.cy, c.cy, v4.c.cy, v4.c.cy, c.cy, c.cy, c.cy],
      op: [0,    0,    0,    0,    1,      1,      0,    0,    1,       1,       0,    0,    0   ],
    },
    { // Circle D — visible in Venn4 only
      cx: [c.cx, c.cx, c.cx, c.cx, c.cx, c.cx, c.cx, c.cx, v4.d.cx, v4.d.cx, c.cx, c.cx, c.cx],
      cy: [c.cy, c.cy, c.cy, c.cy, c.cy, c.cy, c.cy, c.cy, v4.d.cy, v4.d.cy, c.cy, c.cy, c.cy],
      op: [0,    0,    0,    0,    0,    0,    0,    0,    1,       1,       0,    0,    0   ],
    },
  ];
}

// ---------------------------------------------------------------------------
// Bounce mode keyframes — 2 circles bouncing with diagonal crosses & reversals
// ---------------------------------------------------------------------------
// Path mixes adjacent, diagonal, and reverse moves to feel organic:
//   TL → TR (right) → BL (diagonal) → TL (up/reverse) →
//   BR (diagonal) → BL (left/reverse) → TR (diagonal) → BR (down) → TL (diagonal/loop)
//
// 8 moves = 17 stops (hold-start + hold-end per position, plus final loop-back).

// prettier-ignore
const BOUNCE_TIMES = [
  0,     0.04,        // TL hold
  0.125, 0.165,       // TR hold
  0.25,  0.29,        // BL hold
  0.375, 0.415,       // TL hold
  0.5,   0.54,        // BR hold
  0.625, 0.665,       // BL hold
  0.75,  0.79,        // TR hold
  0.875, 0.915,       // BR hold
  1,                   // TL (loop)
];

function buildBounceKeyframes(spread: number) {
  const d = 7 * spread;
  const tl: Pos = { cx: CENTER - d, cy: CENTER - d };
  const tr: Pos = { cx: CENTER + d, cy: CENTER - d };
  const br: Pos = { cx: CENTER + d, cy: CENTER + d };
  const bl: Pos = { cx: CENTER - d, cy: CENTER + d };

  // Lead path:  TL  TL  TR  TR  BL  BL  TL  TL  BR  BR  BL  BL  TR  TR  BR  BR  TL
  // Trail path: BR  BR  TL  TL  TR  TR  BL  BL  TL  TL  BR  BR  BL  BL  TR  TR  BR
  //             (1 step behind the lead)

  // prettier-ignore
  return [
    { // Lead circle
      cx: [tl.cx, tl.cx, tr.cx, tr.cx, bl.cx, bl.cx, tl.cx, tl.cx, br.cx, br.cx, bl.cx, bl.cx, tr.cx, tr.cx, br.cx, br.cx, tl.cx],
      cy: [tl.cy, tl.cy, tr.cy, tr.cy, bl.cy, bl.cy, tl.cy, tl.cy, br.cy, br.cy, bl.cy, bl.cy, tr.cy, tr.cy, br.cy, br.cy, tl.cy],
      op: [1,     1,     1,     1,     1,     1,     1,     1,     1,     1,     1,     1,     1,     1,     1,     1,     1    ],
    },
    { // Trail circle — 1 step behind, lower opacity
      cx: [br.cx, br.cx, tl.cx, tl.cx, tr.cx, tr.cx, bl.cx, bl.cx, tl.cx, tl.cx, br.cx, br.cx, bl.cx, bl.cx, tr.cx, tr.cx, br.cx],
      cy: [br.cy, br.cy, tl.cy, tl.cy, tr.cy, tr.cy, bl.cy, bl.cy, tl.cy, tl.cy, br.cy, br.cy, bl.cy, bl.cy, tr.cy, tr.cy, br.cy],
      op: [0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6,   0.6  ],
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Animated circle choreography loader for Evidence chat.
 *
 * Sequence: 2 circles -> merge -> 3 circles -> merge -> 4 circles -> merge -> loop.
 * Stroke-only by default, inherits color from parent via `currentColor`.
 */
export const EvidenceCircleLoader: FC<EvidenceCircleLoaderProps> = ({
  size = 48,
  className,
  duration = 6,
  mode = 'venn',
  strokeWidth = DEFAULT_STROKE_WIDTH,
  fillOpacity = 0,
  blendMode = 'normal',
  spread = 1,
  formation = 'diamond',
  gooey = false,
  gooeyBlur = 4,
  gooeyContrast = 20,
  easing = 'easeInOut',
}) => {
  const [reduced, setReduced] = useState(false);
  const filterId = useId();

  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return;

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => setReduced(Boolean(mq.matches));
    handleChange();

    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const isBounce = mode === 'bounce';

  const circles = useMemo(
    () => (isBounce ? buildBounceKeyframes(spread) : buildKeyframes(spread, formation)),
    [isBounce, spread, formation]
  );

  const times = isBounce ? BOUNCE_TIMES : TIMES;

  // When gooey is on, force filled circles for the effect to work
  const effectiveFill = gooey
    ? 'currentColor'
    : fillOpacity > 0
      ? 'currentColor'
      : 'none';
  const effectiveFillOpacity = gooey ? 1 : fillOpacity;
  const effectiveStroke = gooey ? 'none' : 'currentColor';
  const effectiveStrokeWidth = gooey ? 0 : strokeWidth;

  if (reduced) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        className={cn('shrink-0', className)}
        aria-hidden="true"
      >
        <circle
          cx={CENTER}
          cy={CENTER}
          r={DEFAULT_RADIUS}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
        />
      </svg>
    );
  }

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      className={cn('shrink-0', className)}
      aria-hidden="true"
      role="img"
    >
      {/* Gooey / metaball filter definition */}
      {gooey && (
        <defs>
          <filter id={filterId}>
            <feGaussianBlur in="SourceGraphic" stdDeviation={gooeyBlur} result="blur" />
            <feColorMatrix
              in="blur"
              type="matrix"
              values={`1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 ${gooeyContrast} -${gooeyContrast / 2}`}
              result="goo"
            />
          </filter>
        </defs>
      )}

      <g filter={gooey ? `url(#${filterId})` : undefined}>
        {circles.map((circle, i) => (
          <m.circle
            key={`${mode}-${formation}-${i}`}
            r={DEFAULT_RADIUS}
            stroke={effectiveStroke}
            strokeWidth={effectiveStrokeWidth}
            fill={effectiveFill}
            fillOpacity={effectiveFillOpacity}
            style={{ mixBlendMode: blendMode }}
            animate={{
              cx: circle.cx,
              cy: circle.cy,
              opacity: circle.op,
            }}
            transition={{
              duration,
              times,
              repeat: Infinity,
              ease: EASING_MAP[easing],
            }}
          />
        ))}
      </g>
    </svg>
  );
};
